import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  OrderReviewModalPage,
  OrderReviewModalPageIdentifier,
  OrderReviewModalPageResult,
} from '../order-review-modal/order-review-modal.page';
import {
  OrderUpdateModalPage,
  OrderUpdateModalPageIdentifier,
  OrderUpdateModalPageResult,
} from '../order-update-modal/order-update-modal.page';
import { PreviousOrderModalPage, PreviousOrderModalPageIdentifier } from '../previous-order-modal/previous-order-modal.page';

import { OrderType } from '../../core/enums/order-type.enum';

import { OrderCaterer } from '../../core/models/order-caterer.model';
import { Order } from '../../core/models/order.model';

import { AnalyticsService } from '../../core/services/analytics.service';
import { ContactService } from '../../core/services/contact.service';
import { DateTimeService } from '../../core/services/date-time.service';
import { LoggerService } from '../../core/services/logger.service';
import { ModalService } from '../../core/services/modal.service';
import { OrdersService } from '../../core/services/orders.service';
import { OverlayService } from '../../core/services/overlay.service';
import { ReferralProgramService } from '../../core/services/referral-program.service';
import { SettingsService } from '../../core/services/settings.service';
import { PlatformService } from '../../core/services/ssr/platform.service';
import { UniversalLinksService } from '../../core/services/universal-links.service';
import { ValidationService } from '../../core/services/validation.service';

import { AnalyticsConfig, TOKEN_ANALYTICS_CONFIG } from '../../core/config/analytics.config';

import { Helpers } from '../../core/helpers';

export const OrderDetailsModalPageIdentifier = 'order-details-modal';

export interface OrderDetailsModalPageResult {
  wasUpdated: boolean;
  wasReviewed: boolean;
  wasCancelled: boolean;
}

@Component({
  selector: 'app-order-details-modal',
  templateUrl: './order-details-modal.page.html',
  styleUrls: ['./order-details-modal.page.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class OrderDetailsModalPage implements OnInit, OnDestroy {
  @Input()
  public order: Order;

  @Input()
  public vendor: OrderCaterer;

  public total: number;
  public subtotal: number;

  public isBrowser: boolean;
  public isWhatsAppChatEnabled = false;
  public isOrderOptionsModalOpen = false;
  public wasReviewed = false;

  private wasUpdated = false;
  private wasCancelled = false;

  private unsubscribe$ = new Subject<void>();

  constructor(
    private contactService: ContactService,
    private overlayService: OverlayService,
    private modalService: ModalService,
    private validationService: ValidationService,
    private referralProgramService: ReferralProgramService,
    private ordersService: OrdersService,
    private loggerService: LoggerService,
    private translateService: TranslateService,
    private universalLinksService: UniversalLinksService,
    private settingsService: SettingsService,
    private platformService: PlatformService,
    private analyticsService: AnalyticsService,
    private dateTimeService: DateTimeService,
    @Inject(TOKEN_ANALYTICS_CONFIG) private analyticsConfig: AnalyticsConfig,
  ) {}

  ngOnInit(): void {
    // IMPORTANT: vendor.total has the discount applied already
    // but we don't want to show the discounts here on this page
    // https://bilbayt.atlassian.net/browse/CA-2828
    this.total = Helpers.sumWithPrecision(this.vendor.total, this.vendor.discount ? this.vendor.discount : 0);
    this.subtotal = Helpers.subtractWithPrecision(this.total, this.vendor.deliveryCharge ? this.vendor.deliveryCharge : 0);

    this.isBrowser = this.platformService.isBrowser;
    this.isWhatsAppChatEnabled = this.settingsService.isWhatsAppChatEnabled();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  public onDismiss(): Promise<boolean> {
    const result: OrderDetailsModalPageResult = {
      wasUpdated: this.wasUpdated,
      wasReviewed: this.wasReviewed,
      wasCancelled: this.wasCancelled,
    };

    return this.modalService.dismissModal({ id: OrderDetailsModalPageIdentifier, data: result });
  }

  public onOpenMoreOptions(): void {
    this.isOrderOptionsModalOpen = true;
  }

  public onDismissOrderOptions(): void {
    this.isOrderOptionsModalOpen = false;
  }

  public onCallUs(): void {
    this.isOrderOptionsModalOpen = false;
    void this.overlayService.showLoading().then(() => this.contactService.callBilbayt().then(() => this.overlayService.hideLoading()));
  }

  public onEmailUs(): void {
    this.isOrderOptionsModalOpen = false;
    void this.overlayService.showLoading().then(() => this.contactService.emailBilbayt().then(() => this.overlayService.hideLoading()));
  }

  public onTextUs(): void {
    this.isOrderOptionsModalOpen = false;
    this.contactService.startChatWithBilbayt();
  }

  public onReorder(): void {
    this.isOrderOptionsModalOpen = false;

    void this.modalService.showSheet({
      id: PreviousOrderModalPageIdentifier,
      component: PreviousOrderModalPage,
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      componentProps: {
        ordersVendors: [{ orderId: this.order.orderId, vendorId: this.vendor.catererId }],
      },
    });
  }

  public onOpenOrderReviewModal(): void {
    this.isOrderOptionsModalOpen = false;
    void this.modalService
      .showModal<OrderReviewModalPageResult>({
        component: OrderReviewModalPage,
        id: OrderReviewModalPageIdentifier,
        componentProps: { orderId: this.order.orderId },
      })
      .then((result) => {
        if (result && result.reviewed) {
          this.wasReviewed = true;
        }
      });
  }

  public onOpenOrderUpdateModal(): void {
    this.isOrderOptionsModalOpen = false;
    void this.modalService
      .showAlert<OrderUpdateModalPageResult>({
        component: OrderUpdateModalPage,
        id: OrderUpdateModalPageIdentifier,
        componentProps: { orderId: this.order.orderId, vendorId: this.vendor.catererId, vendorName: this.vendor.name },
      })
      .then((result) => {
        if (result && result.updated) {
          this.wasUpdated = true;
        }
      });
  }

  public onShareOrder(): void {
    this.isOrderOptionsModalOpen = false;
    this.trackShareEvent();

    void this.referralProgramService.maybeShowReferralProgramModal().then((shouldShare) => {
      if (shouldShare) {
        // Since we're using the eventDateTime, both the date and the time will be defined
        // even if the order is based on time slots or for now
        const dateStr = this.dateTimeService.format({ dateTimeIso: this.order.eventDateTime, format: 'MMMM DD, YYYY' });
        const timeStr = this.dateTimeService.format({ dateTimeIso: this.order.eventDateTime, format: 'HH:mm' });
        const isForNow = this.order.orderType === OrderType.BilbaytNow;

        this.universalLinksService.shareOrderDetails({ isForNow, dateStr, timeStr }).catch((error: unknown) => {
          const errorMessage = this.translateService.instant('ERROR_MESSAGE.SHARE') as string;

          this.loggerService.info({
            component: 'OrderDetailsModalPage',
            message: "couldn't share order details",
            details: { error, messageShownToUser: errorMessage },
          });

          this.overlayService.showToast({ type: 'error', message: errorMessage });
        });
      }
    });
  }

  public onCancelOrder(): void {
    this.isOrderOptionsModalOpen = false;

    const cancelOrderCallback = () => {
      void this.overlayService.showLoading().then(() => {
        this.ordersService
          .cancelOrder(this.order.orderId)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: () => {
              // The order has been cancelled, so now we update
              // the details being shown on this page

              this.ordersService
                .getOrderDetails(this.order.orderId)
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe({
                  next: (orderDetails) => {
                    orderDetails.caterers.sort((a, b) => this.dateTimeService.sort(a.deliveryDate.min, b.deliveryDate.min));

                    this.order = orderDetails;
                    this.wasCancelled = true;

                    void this.modalService.showAlert({
                      title: this.translateService.instant('ORDER_DETAILS_MODAL_PAGE.CANCEL_ORDER') as string,
                      message: this.translateService.instant('ORDER_DETAILS_MODAL_PAGE.CANCEL_ORDER_ALERT_SUCCESS') as string,
                    });

                    this.trackCancelOrderEvent();
                    void this.overlayService.hideLoading();
                  },
                  error: (error: HttpErrorResponse) => {
                    const errorMessage = this.validationService.getErrorMessage(error);

                    this.loggerService.info({
                      component: 'OrderDetailsModalPage',
                      message: "couldn't get order details after cancelling",
                      details: { error, messageShownToUser: errorMessage },
                    });

                    void this.overlayService.hideLoading().then(() =>
                      this.modalService.showAlert({
                        title: this.translateService.instant('ERROR') as string,
                        message: errorMessage,
                      }),
                    );
                  },
                });
            },
            error: (error: HttpErrorResponse) => {
              const errorMessage = this.validationService.getErrorMessage(error);

              this.loggerService.info({
                component: 'OrderDetailsModalPage',
                message: "couldn't request order to be cancelled",
                details: { error, messageShownToUser: errorMessage },
              });

              void this.overlayService
                .hideLoading()
                .then(() =>
                  this.modalService.showAlert({ title: this.translateService.instant('ERROR') as string, message: errorMessage }),
                );
            },
          });
      });
    };

    void this.modalService.showAlert({
      title: this.translateService.instant('ORDER_DETAILS_MODAL_PAGE.CANCEL_ORDER') as string,
      message: this.translateService.instant('ORDER_DETAILS_MODAL_PAGE.CANCEL_ORDER_ALERT_MESSAGE') as string,
      buttons: [
        { isDestructive: true, text: this.translateService.instant('YES') as string, handler: cancelOrderCallback },
        { text: this.translateService.instant('NO') as string },
      ],
    });
  }

  private trackCancelOrderEvent(): void {
    void this.analyticsService.trackEvent({
      name: this.analyticsConfig.eventName.orderDetailsPageCancelOrder,
      data: {
        [this.analyticsConfig.propertyName.orderId]: this.order.orderId,
      },
    });
  }

  private trackShareEvent(): void {
    const isReferralProgramEnabled = this.settingsService.isReferralProgramFeatureEnabled();

    void this.analyticsService.trackEvent({
      name: this.analyticsConfig.eventName.share,
      data: {
        [this.analyticsConfig.propertyName.orderId]: this.order.orderId,
        [this.analyticsConfig.propertyName.referralProgram]: !!isReferralProgramEnabled,
      },
    });

    void this.analyticsService.trackEvent({
      name: this.analyticsConfig.eventName.orderDetailsPageShare,
      data: {
        [this.analyticsConfig.propertyName.orderId]: this.order.orderId,
        [this.analyticsConfig.propertyName.referralProgram]: !!isReferralProgramEnabled,
      },
    });
  }
}
